import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useContent } from '../hooks/useContent';
import { useSite } from '../hooks/useSites';
import { useToast } from '../hooks/useToast';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { useTemplateManifest } from '../hooks/useTemplateManifest';
import { useEditorPreview } from '../hooks/useEditorPreview';
import { getSchema } from '../api/schemas';
import { getContentId } from '../types';
import { firstMissingRequiredField } from '../utils/validateRequired';
import FormField from '../components/FormField';
import HelpPopover from '../components/HelpPopover';
import { pageHelp } from '../data/helpContent';
import '../styles/editor.css';

export default function SiteEditor() {
  const { siteId } = useParams<{ siteId: string }>();
  const { site } = useSite(siteId);
  const { items, loading, create, update } = useContent(siteId, 'site');
  const { addToast } = useToast();
  const { filterFields } = useTemplateManifest(site?.templateId);
  const { sendUpdate } = useEditorPreview('/');

  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [originalData, setOriginalData] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);

  const schema = site ? getSchema(site.templateId) : undefined;
  const rawContentType = schema?.contentTypes.site;
  const filteredContentType = rawContentType ? filterFields('site', rawContentType) : undefined;
  const fields = filteredContentType?.fields || {};
  const existingItem = items[0];

  const isDirty = JSON.stringify(formData) !== JSON.stringify(originalData);
  useUnsavedChanges(isDirty);

  useEffect(() => {
    if (existingItem) {
      const data = existingItem.data || {};
      setFormData(data);
      setOriginalData(data);
    }
  }, [existingItem]);

  const handleChange = (name: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    sendUpdate('site', name, value);
  };

  const handleSave = async () => {
    if (!siteId) return;
    const missing = firstMissingRequiredField(fields, formData);
    if (missing) {
      addToast('error', `Please fill in "${missing}" before saving`);
      return;
    }
    setSaving(true);
    try {
      if (existingItem) {
        const contentId = getContentId(existingItem.contentKey);
        await update(contentId, formData);
      } else {
        await create(formData);
      }
      setOriginalData({ ...formData });
      addToast('success', 'Site settings saved');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="loading-state"><div className="spinner" /></div>
      </div>
    );
  }

  const help = pageHelp.site;

  return (
    <div className="page">
      <div className="page-header">
        <h1>Site Settings</h1>
        <div className="page-header-actions">
          <HelpPopover title={help.title} description={help.description} tips={help.tips} />
          {isDirty && <span className="unsaved-indicator">Unsaved changes</span>}
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      <div className="editor-form">
        {Object.entries(fields).map(([name, fieldSchema]) => (
          <FormField
            key={name}
            name={name}
            schema={fieldSchema}
            value={formData[name]}
            onChange={handleChange}
            siteId={siteId!}
            cmsType="site"
          />
        ))}
      </div>
    </div>
  );
}
